// SsoIntegrationsPage.tsx — manages OAuth/OIDC relying parties for this issuer.
import { useState, useEffect, useCallback } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import Modal from 'react-bootstrap/Modal'
import { BsArrowClockwise, BsPlusLg, BsTrash, BsX } from 'react-icons/bs'
import NameTag from '../components/NameTag'
import Text from 'common/ui/Text'
import { useConfirm } from 'common/ui/ConfirmDialog'
import { useAuth, apiFetch } from '../hooks/useAuth'

interface OAuthClient {
    client_id: string
    name: string
    redirect_uris: string[]
    scopes?: string[]
    created_at: string
}

interface CreateResponse {
    client: OAuthClient
    client_secret: string
}

const SsoIntegrationsPage = () => {
    const { email, loading, error: authError } = useAuth()
    const [clients, setClients] = useState<OAuthClient[]>([])
    const [loadError, setLoadError] = useState('')

    const [showCreate, setShowCreate] = useState(false)
    const [newID, setNewID] = useState('')
    const [newName, setNewName] = useState('')
    const [newRedirects, setNewRedirects] = useState('')
    const [newScopes, setNewScopes] = useState('')
    const [formError, setFormError] = useState('')
    // Failures from the row actions surface as an outlined alert on the
    // page rather than a native alert() (DESIGN.md C-MSG-1 / C-MSG-3).
    const [actionError, setActionError] = useState('')

    // The plaintext secret is shown exactly once, immediately after create or rotate.
    const [revealedSecret, setRevealedSecret] = useState<{ clientID: string; secret: string } | null>(null)

    const fetchClients = useCallback(async () => {
        try {
            const res = await apiFetch('/api/admin/oauth-clients')
            if (!res.ok) {
                setLoadError('Failed to load SSO integrations')
                return
            }
            const data = await res.json()
            setClients(data.clients || [])
        } catch {
            setLoadError('Failed to load SSO integrations')
        }
    }, [])

    useEffect(() => {
        if (!loading && email) fetchClients()
    }, [loading, email, fetchClients])

    const resetCreateForm = () => {
        setNewID('')
        setNewName('')
        setNewRedirects('')
        setNewScopes('')
        setFormError('')
    }

    const closeCreateModal = () => {
        setShowCreate(false)
        resetCreateForm()
    }

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault()
        setFormError('')
        const redirect_uris = newRedirects.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
        const scopesArr = newScopes.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
        if (!newID || !newName || redirect_uris.length === 0) {
            setFormError('client_id, name, and at least one redirect URI are required')
            return
        }
        try {
            const res = await apiFetch('/api/admin/oauth-clients', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: newID,
                    name: newName,
                    redirect_uris,
                    scopes: scopesArr,
                }),
            })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                setFormError(data.error || 'Failed to create integration')
                return
            }
            const data: CreateResponse = await res.json()
            setRevealedSecret({ clientID: data.client.client_id, secret: data.client_secret })
            resetCreateForm()
            setShowCreate(false)
            fetchClients()
        } catch {
            setFormError('Failed to create integration')
        }
    }

    const [confirm, confirmDialog] = useConfirm()

    const handleDelete = async (clientID: string) => {
        const ok = await confirm({
            title: 'Delete SSO Integration',
            body: <>Delete SSO integration <code>{clientID}</code>? Issued tokens remain valid until they expire.</>,
            confirmLabel: 'Delete',
        })
        if (!ok) return
        setActionError('')
        try {
            const res = await apiFetch(`/api/admin/oauth-clients/${encodeURIComponent(clientID)}`, { method: 'DELETE' })
            if (!res.ok) { setActionError(`Failed to delete ${clientID}.`); return }
            fetchClients()
        } catch {
            setActionError(`Failed to delete ${clientID}.`)
        }
    }

    const handleRotate = async (clientID: string) => {
        const ok = await confirm({
            title: 'Rotate Client Secret',
            body: <>Rotate <code>client_secret</code> for <code>{clientID}</code>? The current secret stops working immediately.</>,
            confirmLabel: 'Rotate',
            variant: 'warning',
        })
        if (!ok) return
        setActionError('')
        try {
            const res = await apiFetch(`/api/admin/oauth-clients/${encodeURIComponent(clientID)}/rotate-secret`, { method: 'POST' })
            if (!res.ok) { setActionError(`Failed to rotate the secret for ${clientID}.`); return }
            const data: CreateResponse = await res.json()
            setRevealedSecret({ clientID: data.client.client_id, secret: data.client_secret })
        } catch {
            setActionError(`Failed to rotate the secret for ${clientID}.`)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h1 className="m-0">SSO Integrations</h1>
                <Button variant="success" onClick={() => setShowCreate(true)} className="d-flex align-items-center gap-2">
                    <BsPlusLg /> New Integration
                </Button>
            </div>

            {authError && <Alert variant="danger">{authError}</Alert>}
            {loadError && <Alert variant="warning">{loadError}</Alert>}
            {actionError && (
                <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert>
            )}

            {revealedSecret && (
                <Alert variant="warning">
                    <Alert.Heading className="h6">SAVE THIS CLIENT_SECRET</Alert.Heading>
                    <div>{revealedSecret.clientID}</div>
                    <div className="small mt-1">
                        It will not be shown again. Configure your relying party with it now.
                    </div>
                    <pre className="mt-2 mb-2 p-2 bg-body-secondary rounded" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {revealedSecret.secret}
                    </pre>
                    <Button variant="secondary" size="sm" onClick={() => setRevealedSecret(null)}>
                        I saved it
                    </Button>
                </Alert>
            )}

            {clients.length > 0 ? (
                <Card className="mb-3">
                    <Card.Header>Integrations</Card.Header>
                    <Card.Body>
                        <Table size="sm" hover responsive className="m-0">
                            <thead>
                                <tr>
                                    <th>Client ID</th>
                                    <th>Name</th>
                                    <th>Redirect URIs</th>
                                    <th>Scopes</th>
                                    <th style={{ width: '180px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((c) => (
                                    <tr key={c.client_id}>
                                        <td><code>{c.client_id}</code></td>
                                        <td>{c.name}</td>
                                        <td>
                                            {c.redirect_uris.map((u) => (
                                                <div key={u}><code>{u}</code></div>
                                            ))}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-2">
                                                {c.scopes && c.scopes.length > 0
                                                    ? c.scopes.map((s) => (
                                                        <NameTag
                                                            key={s}
                                                            name={s}
                                                            kind="scope"
                                                            to={`/scopes/${encodeURIComponent(s)}`}
                                                        />
                                                    ))
                                                    : <span className="text-muted small">—</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button variant="warning" size="sm" onClick={() => handleRotate(c.client_id)} title="Rotate client_secret" className="d-flex align-items-center gap-1">
                                                    <BsArrowClockwise /> Rotate
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(c.client_id)}
                                                    title="Delete"
                                                    className="d-flex align-items-center gap-1"
                                                >
                                                    <BsTrash />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            ) : (
                <Card className="mb-3">
                    <Card.Body>
                        <span className="text-muted small">
                            No SSO integrations yet. Add one to let an external app delegate auth here.
                        </span>
                    </Card.Body>
                </Card>
            )}

            <Modal show={showCreate} onHide={closeCreateModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>New SSO Integration</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    <Form id="sso-create-form" onSubmit={handleCreate}>
                        <Form.Group className="mb-3" controlId="sso-client-id">
                            <Form.Label>client_id</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="e.g. gitea"
                                value={newID}
                                onChange={(e) => setNewID(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="sso-display-name">
                            <Form.Label>Display name</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="e.g. Gitea"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="sso-redirects">
                            <Form.Label>Redirect URIs (whitespace or comma separated)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="https://gitea.lab0.us/user/oauth2/authz/callback"
                                value={newRedirects}
                                onChange={(e) => setNewRedirects(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="sso-scopes">
                            <Form.Label>Allowed scopes</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="openid profile email gitea.user"
                                value={newScopes}
                                onChange={(e) => setNewScopes(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeCreateModal} className="d-flex align-items-center gap-2">
                        <BsX /> Cancel
                    </Button>
                    <Button variant="success" type="submit" form="sso-create-form" className="d-flex align-items-center gap-2">
                        <BsPlusLg /> Create
                    </Button>
                </Modal.Footer>
            </Modal>

            {confirmDialog}
        </>
    )
}

export default SsoIntegrationsPage
